import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Factory, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { authApi, formatApiErrorDetail } from "../lib/authApi";

const initialForm = {
  company_name: "", contact_name: "", email: "", password: "", phone: "",
  city: "", state: "", gst_number: "", certifications: "", factory_size: "", years_in_business: "",
};

export const ProcessChips = ({ options, selected, onToggle, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((p) => {
      const active = selected.includes(p);
      return (
        <button key={p} type="button" onClick={() => onToggle(p)}
          data-testid={`${testPrefix}-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`px-3.5 py-1.5 rounded-full border text-xs font-medium transition-colors duration-200 ${
            active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:border-primary/50"
          }`}>
          {p}
        </button>
      );
    })}
  </div>
);

export default function PartnerRegister() {
  const [form, setForm] = useState(initialForm);
  const [processes, setProcesses] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    authApi.get("/library/pricing-config").then((r) => setOptions(r.data.processes.map((p) => p.name))).catch(() => {});
  }, []);

  const toggleProcess = (p) => setProcesses((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.password) {
      toast.error("Company, contact name, email and password are required.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await authApi.post("/partner/register", {
        ...form,
        processes,
        certifications: form.certifications.split(",").map((x) => x.trim()).filter(Boolean),
        years_in_business: form.years_in_business === "" ? null : parseInt(form.years_in_business, 10),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-24 px-4" data-testid="partner-register-success">
        <div className="w-full max-w-md p-10 rounded-xl bg-card border border-border text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-5" strokeWidth={1.5} />
          <h1 className="text-2xl font-medium font-heading tracking-tight">Application Submitted</h1>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Thanks! Our partnerships team will review your application. You'll receive an email once approved — then you can sign in to the Partner Portal.
          </p>
          <Button onClick={() => navigate("/partner/login")} className="mt-6 rounded-full px-8" data-testid="partner-register-goto-login-btn">
            Go to Partner Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-24 px-4" data-testid="partner-register-page">
      <div className="w-full max-w-2xl p-8 sm:p-10 rounded-xl bg-card border border-border">
        <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
          <Factory className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl sm:text-3xl font-medium font-heading tracking-tight">Become a Manufacturing Partner</h1>
        <p className="mt-2 text-sm text-muted-foreground">Apply with your capabilities. Approved partners get access to the Partner Portal.</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="partner-register-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={set("company_name")} placeholder="Acme Precision Pvt Ltd" data-testid="preg-company-input" />
            </div>
            <div className="space-y-2">
              <Label>Contact Person *</Label>
              <Input value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" data-testid="preg-contact-input" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="preg-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Password * <span className="text-muted-foreground font-normal">(min 8 chars)</span></Label>
              <Input type="password" value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="preg-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={set("phone")} placeholder="+91 ..." data-testid="preg-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>GST Number</Label>
              <Input value={form.gst_number} onChange={set("gst_number")} placeholder="22AAAAA0000A1Z5" data-testid="preg-gst-input" />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={form.city} onChange={set("city")} placeholder="Pune" data-testid="preg-city-input" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input value={form.state} onChange={set("state")} placeholder="Maharashtra" data-testid="preg-state-input" />
            </div>
            <div className="space-y-2">
              <Label>Factory Size</Label>
              <Input value={form.factory_size} onChange={set("factory_size")} placeholder="e.g. 10,000 sq ft" data-testid="preg-factory-input" />
            </div>
            <div className="space-y-2">
              <Label>Years in Business</Label>
              <Input type="number" min="0" value={form.years_in_business} onChange={set("years_in_business")} placeholder="e.g. 12" data-testid="preg-years-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Manufacturing Processes Offered</Label>
            <ProcessChips options={options} selected={processes} onToggle={toggleProcess} testPrefix="preg-process" />
          </div>
          <div className="space-y-2">
            <Label>Certifications <span className="text-muted-foreground font-normal">(comma-separated)</span></Label>
            <Input value={form.certifications} onChange={set("certifications")} placeholder="ISO 9001:2015, IATF 16949" data-testid="preg-certs-input" />
          </div>
          <Button type="submit" size="lg" disabled={loading} className="w-full rounded-full" data-testid="preg-submit-btn">
            {loading ? "Submitting..." : "Submit Application"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-muted-foreground text-center">
          Already approved?{" "}
          <Link to="/partner/login" className="text-primary font-medium hover:underline" data-testid="goto-partner-login-link">
            Partner Login
          </Link>
        </p>
      </div>
    </div>
  );
}
